import { Action, createAsyncAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import {
  CustomerInformationType,
  FieldValidationResultType,
  OrderType,
  PostPaymentOrderError,
  PostPaymentOrderRequest,
  PostPaymentOrderResponse,
  ProfileForm,
} from '@dtp/membership-service-types';
import { FieldObject, ProfileData, UpdateProfileSuccess } from '../../../../types/profileType';
import { GetHouseholdMemberResponse } from '../../../../types/householdMembers';

// Action Types
export enum Actions {
  GET_HOUSEHOLD_MEMBERS = 'naf/householdMembers/GET_HOUSEHOLD_MEMBERS',
  GET_HOUSEHOLD_MEMBERS_SUCCESS = 'naf/householdMembers/GET_HOUSEHOLD_MEMBERS_SUCCESS',
  GET_HOUSEHOLD_MEMBERS_FAIL = 'naf/householdMembers/GET_HOUSEHOLD_MEMBERS_FAIL',
  GET_HOUSEHOLD_MEMBERS_CANCEL = 'naf/householdMembers/GET_HOUSEHOLD_MEMBERS_CANCEL',
  GET_HOUSEHOLD_MEMBER_DETAILS = 'naf/householdMembers/GET_HOUSEHOLD_MEMBER_DETAILS',
  GET_HOUSEHOLD_MEMBER_DETAILS_SUCCESS = 'naf/householdMembers/GET_HOUSEHOLD_MEMBER_DETAILS_SUCCESS',
  GET_HOUSEHOLD_MEMBER_DETAILS_FAIL = 'naf/householdMembers/GET_HOUSEHOLD_MEMBER_DETAILS_FAIL',
  GET_HOUSEHOLD_MEMBER_DETAILS_CANCEL = 'naf/householdMembers/GET_HOUSEHOLD_MEMBER_DETAILS_CANCEL',
  CREATE_HOUSEHOLD_MEMBER_ORDER = 'naf/householdMembers/CREATE_HOUSEHOLD_MEMBER_ORDER',
  CREATE_HOUSEHOLD_MEMBER_ORDER_SUCCESS = 'naf/householdMembers/CREATE_HOUSEHOLD_MEMBER_ORDER_SUCCESS',
  CREATE_HOUSEHOLD_MEMBER_ORDER_FAIL = 'naf/householdMembers/CREATE_HOUSEHOLD_MEMBER_ORDER_FAIL',
  CREATE_HOUSEHOLD_MEMBER_ORDER_CANCEL = 'naf/householdMembers/CREATE_HOUSEHOLD_MEMBER_ORDER_CANCEL',
  UPDATE_HOUSEHOLD_MEMBER = 'naf/householdMembers/UPDATE_HOUSEHOLD_MEMBER',
  UPDATE_HOUSEHOLD_MEMBER_SUCCESS = 'naf/householdMembers/UPDATE_HOUSEHOLD_MEMBER_SUCCESS',
  UPDATE_HOUSEHOLD_MEMBER_FAIL = 'naf/householdMembers/UPDATE_HOUSEHOLD_MEMBER_FAIL',
  UPDATE_HOUSEHOLD_MEMBER_CANCEL = 'naf/householdMembers/UPDATE_HOUSEHOLD_MEMBER_CANCEL',
}

// State Type
export interface State {
  meta: { isUpdating: boolean; fetchState: string };
  data: {
    mainHouseholdMemberFullName: string;
    isMainHouseholdMember: boolean;
    householdMembers: Record<
      CustomerInformationType['customerId'],
      CustomerInformationType & {
        meta: { isUpdating: boolean };
        data: Partial<ProfileForm>;
        errorState?: ReduxError;
        validation?: Record<string, string | null>;
      }
    >;
    order: {
      salesOrder?: OrderType;
      validation?: PostPaymentOrderError;
      isCreating: boolean;
      orderDetails?: PostPaymentOrderResponse;
      isCreated: boolean;
    };
  };
  errorState?: any;
}

export const initialState: State = {
  meta: { isUpdating: false, fetchState: 'initial' },
  data: {
    householdMembers: {},
    mainHouseholdMemberFullName: '',
    isMainHouseholdMember: false,
    order: {
      salesOrder: {
        customerProductsRequests: [],
        returnUrl: '',
      },
      isCreated: false,
      isCreating: false,
    },
  },
};

// Actions
export const actions = {
  getHouseholdMembers: createAsyncAction(
    Actions.GET_HOUSEHOLD_MEMBERS,
    Actions.GET_HOUSEHOLD_MEMBERS_SUCCESS,
    Actions.GET_HOUSEHOLD_MEMBERS_FAIL,
    Actions.GET_HOUSEHOLD_MEMBERS_CANCEL,
  )<undefined, GetHouseholdMemberResponse, ReduxError, undefined>(),
  getHouseholdMemberDetails: createAsyncAction(
    Actions.GET_HOUSEHOLD_MEMBER_DETAILS,
    Actions.GET_HOUSEHOLD_MEMBER_DETAILS_SUCCESS,
    Actions.GET_HOUSEHOLD_MEMBER_DETAILS_FAIL,
    Actions.GET_HOUSEHOLD_MEMBER_DETAILS_CANCEL,
  )<
    CustomerInformationType['customerId'],
    [ProfileForm, CustomerInformationType['customerId']],
    [ReduxError, CustomerInformationType['customerId']],
    [undefined, CustomerInformationType['customerId']]
  >(),
  createOrder: createAsyncAction(
    Actions.CREATE_HOUSEHOLD_MEMBER_ORDER, // request payload creator
    Actions.CREATE_HOUSEHOLD_MEMBER_ORDER_SUCCESS, // success payload creator
    Actions.CREATE_HOUSEHOLD_MEMBER_ORDER_FAIL, // failure payload creator
    Actions.CREATE_HOUSEHOLD_MEMBER_ORDER_CANCEL, // optional cancel payload creator
  )<PostPaymentOrderRequest, PostPaymentOrderResponse, PostPaymentOrderError, undefined>(),
  updateHouseholdMember: createAsyncAction(
    Actions.UPDATE_HOUSEHOLD_MEMBER,
    Actions.UPDATE_HOUSEHOLD_MEMBER_SUCCESS,
    Actions.UPDATE_HOUSEHOLD_MEMBER_FAIL,
    Actions.UPDATE_HOUSEHOLD_MEMBER_CANCEL,
  )<
    [ProfileData, { memberId: string }],
    [UpdateProfileSuccess, { memberId: string }],
    [
      { success: boolean; validation: Record<string, string | null>; profile: ProfileData | null },
      { memberId: string },
    ],
    [undefined, { memberId: string }]
  >(),
};

// Reducers
export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getHouseholdMembers.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.meta.isUpdating = true;
      draftState.meta.fetchState = 'loading';
    }),
  )
  .handleAction(actions.getHouseholdMembers.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.mainHouseholdMemberFullName = action.payload.mainHouseholdMemberFullName;
      draftState.data.isMainHouseholdMember = action.payload.isMainHouseholdMember;
      draftState.data.householdMembers = action.payload.householdMembers.reduce(
        (
          acc: Record<
            CustomerInformationType['customerId'],
            CustomerInformationType & { meta: { isUpdating: boolean }; data: Record<string, FieldObject> }
          >,
          cur,
        ) => {
          acc[cur.customerId] = { ...cur, meta: { isUpdating: false }, data: {} };
          return acc;
        },
        {},
      );
      draftState.meta.isUpdating = false;
      draftState.meta.fetchState = 'success';
    }),
  )
  .handleAction(actions.getHouseholdMembers.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.meta.isUpdating = false;
      draftState.errorState = action.payload;
      draftState.meta.fetchState = 'error';
    }),
  )
  .handleAction(actions.getHouseholdMembers.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.meta.isUpdating = false;
      draftState.meta.fetchState = 'initial';
    }),
  )
  .handleAction(actions.getHouseholdMemberDetails.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.payload].meta.isUpdating = true;
    }),
  )
  .handleAction(actions.getHouseholdMemberDetails.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta].data = action.payload;
      draftState.data.householdMembers[action.meta].meta.isUpdating = false;
      draftState.data.householdMembers[action.meta].errorState = undefined;
    }),
  )
  .handleAction(actions.getHouseholdMemberDetails.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta].errorState = action.payload;
      draftState.data.householdMembers[action.meta].meta.isUpdating = false;
    }),
  )
  .handleAction(actions.getHouseholdMemberDetails.cancel, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta].meta.isUpdating = false;
    }),
  )
  .handleAction(actions.updateHouseholdMember.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta.memberId].meta.isUpdating = true;
    }),
  )
  .handleAction(actions.updateHouseholdMember.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta.memberId].data = action.payload.profile;
    }),
  )
  .handleAction(actions.updateHouseholdMember.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta.memberId].meta.isUpdating = false;
      draftState.data.householdMembers[action.meta.memberId].validation = action.payload.validation;
    }),
  )
  .handleAction(actions.updateHouseholdMember.cancel, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.householdMembers[action.meta.memberId].meta.isUpdating = false;
    }),
  )
  .handleAction(actions.createOrder.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.data.order.isCreating = true;
      delete draftState.data.order.validation;
    }),
  )
  .handleAction(actions.createOrder.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.order.isCreating = false;
      draftState.data.order.isCreated = true;
      draftState.data.order.orderDetails = action.payload;
    }),
  )
  .handleAction(actions.createOrder.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.data.order.isCreating = false;
    }),
  )
  .handleAction(actions.createOrder.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.data.order.isCreating = false;
      draftState.data.order.isCreated = false;
      const validation = action.payload;
      if (validation?.validationResults) {
        validation.validationMap = validation.validationResults.reduce(
          (acc: Record<FieldValidationResultType['field'], FieldValidationResultType>, cur) => {
            acc[cur.field] = cur;
            return acc;
          },
          {},
        );
      }
      draftState.data.order.validation = validation;
    }),
  );
